(() => {
  const OPEN_EVENT = 'yx:pdp-media-viewer:open';
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const TOUCH_QUERY = window.matchMedia('(max-width: 1199px)');
  const FULL_VIEWER_QUERY = window.matchMedia('(min-width: 750px)');
  const DESKTOP_QUERY = window.matchMedia('(min-width: 1200px)');
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
  const IS_SPANISH = (document.documentElement.lang || '').toLowerCase().startsWith('es');
  const COPY = IS_SPANISH ? {
    viewer: 'Visor multimedia del producto',
    media: 'Contenido multimedia del producto',
    previous: 'Contenido anterior',
    next: 'Contenido siguiente',
    close: 'Cerrar visor multimedia',
    zoom: 'Ampliar imagen',
    resetZoom: 'Restablecer ampliación',
    drag: 'Arrastrar para mover la imagen',
    show: 'Mostrar contenido',
    play: 'Reproducir video',
    pause: 'Pausar video',
    of: 'de',
  } : {
    viewer: 'Product media viewer',
    media: 'Product media',
    previous: 'Previous media',
    next: 'Next media',
    close: 'Close media viewer',
    zoom: 'Zoom image',
    resetZoom: 'Reset image zoom',
    drag: 'Drag to move image',
    show: 'Show media',
    play: 'Play video',
    pause: 'Pause video',
    of: 'of',
  };
  let viewer = null;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const normalise = (index, length) => (index % length + length) % length;
  const touchDistance = (first, second) => Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
  const touchMidpoint = (first, second) => ({
    x: (first.clientX + second.clientX) / 2,
    y: (first.clientY + second.clientY) / 2,
  });

  function createViewer() {
    const dialog = document.createElement('dialog');
    dialog.className = 'yx-pdp-media-viewer';
    dialog.setAttribute('aria-label', COPY.viewer);
    dialog.innerHTML = `
      <div class="yx-pdp-media-viewer__stage" data-yx-viewer-stage></div>
      <div class="yx-pdp-media-viewer__thumbs" role="tablist" aria-label="${COPY.media}" data-yx-viewer-thumbs></div>
      <output class="yx-pdp-media-viewer__counter" aria-live="polite">
        <span data-yx-viewer-current>1</span>
        <span class="yx-pdp-media-viewer__counter-slash">/</span>
        <span class="yx-pdp-media-viewer__counter-total" data-yx-viewer-total>1</span>
      </output>
      <button class="yx-pdp-media-viewer__arrow yx-pdp-media-viewer__arrow--prev" type="button" aria-label="${COPY.previous}" data-yx-viewer-step="-1">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14.5 4-8 8 8 8"/></svg>
      </button>
      <button class="yx-pdp-media-viewer__arrow yx-pdp-media-viewer__arrow--next" type="button" aria-label="${COPY.next}" data-yx-viewer-step="1">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9.5 4 8 8-8 8"/></svg>
      </button>
      <button class="yx-pdp-media-viewer__close" type="button" aria-label="${COPY.close}" data-yx-viewer-close></button>`;
    document.body.append(dialog);
    dialog.querySelector('[data-yx-viewer-stage]')?.addEventListener('scroll', () => {
      const state = dialog._yxState;
      if (!state || !FULL_VIEWER_QUERY.matches || state.scrollFrame) return;
      state.scrollFrame = requestAnimationFrame(() => {
        state.scrollFrame = null;
        const width = state.stage.clientWidth;
        if (!width) return;
        const nextIndex = clamp(Math.round(state.stage.scrollLeft / width), 0, state.cards.length - 1);
        if (nextIndex !== state.index) {
          resetZoom(state);
          state.index = nextIndex;
          syncDesktopViewer(state);
        }
      });
    }, { passive: true });
    dialog.addEventListener('wheel', (event) => {
      const state = dialog._yxState;
      if (!state || !dialog.open || !DESKTOP_QUERY.matches || state.cards.length < 2) return;
      if (event.target.closest('.yx-pdp-media-viewer__thumbs')) return;
      const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      if (Math.abs(delta) < 10) return;
      event.preventDefault();
      const now = performance.now();
      if ((state.wheelLockedUntil || 0) > now) return;
      state.wheelLockedUntil = now + 420;
      step(state, delta > 0 ? 1 : -1);
    }, { passive: false });
    return dialog;
  }

  function pauseMedia(container) {
    container?.querySelectorAll('video').forEach((video) => video.pause());
  }

  function sourceMedia(card) {
    const mediaType = card?.dataset.yxMediaType;
    if (mediaType === 'video') return card.querySelector(':scope > video');
    if (mediaType === 'external_video') return card.querySelector(':scope > iframe');
    if (mediaType === 'model') return card.querySelector(':scope > model-viewer');
    return card?.querySelector(':scope > img:not(.main-product__media-preview)');
  }

  function buildMedia(card, position, state, mediaIndex = normalise(state.index + position, state.cards.length)) {
    const item = document.createElement('div');
    const source = sourceMedia(card);
    const mediaType = card?.dataset.yxMediaType || (source?.tagName || '').toLowerCase();
    item.className = 'yx-pdp-media-viewer__item';
    item.dataset.yxViewerPosition = position;
    item.dataset.yxViewerMediaType = mediaType;
    item.setAttribute('role', 'group');
    item.setAttribute('aria-label', `${mediaIndex + 1} ${COPY.of} ${state.cards.length}`);

    if (!source) return item;
    if (source instanceof HTMLImageElement) {
      const wrap = document.createElement('div');
      const image = document.createElement('img');
      wrap.className = 'yx-pdp-media-viewer__image-wrap';
      wrap.tabIndex = 0;
      wrap.setAttribute('role', 'button');
      wrap.setAttribute('aria-label', COPY.zoom);
      image.className = 'yx-pdp-media-viewer__image';
      image.src = source.currentSrc || source.src;
      image.dataset.yxHighRes = source.dataset.yxZoomSrc || source.getAttribute('data_yx_zoom_src') || '';
      image.alt = source.alt || '';
      image.draggable = false;
      image.decoding = 'async';
      wrap.append(image);
      item.append(wrap);
      return item;
    }

    const clone = source.cloneNode(true);
    clone.removeAttribute('autoplay');
    if (clone instanceof HTMLVideoElement) {
      clone.controls = true;
      clone.autoplay = false;
      clone.loop = false;
      clone.preload = 'metadata';
      clone.playsInline = true;
    }
    item.append(clone);
    if (clone instanceof HTMLVideoElement) {
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'yx-pdp-media-viewer__video-toggle';
      toggle.setAttribute('aria-label', COPY.play);
      toggle.innerHTML = `
        <svg class="yx-pdp-media-viewer__video-play" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 10 7-10 7V5Z"/></svg>
        <svg class="yx-pdp-media-viewer__video-pause" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14M16 5v14"/></svg>`;
      const sync = () => {
        toggle.dataset.playing = String(!clone.paused);
        toggle.setAttribute('aria-label', clone.paused ? COPY.play : COPY.pause);
      };
      clone.addEventListener('play', sync);
      clone.addEventListener('pause', sync);
      clone.addEventListener('ended', sync);
      item.append(toggle);
      clone.load();
      sync();
    }
    return item;
  }

  function buildThumbnail(card, index) {
    const button = document.createElement('button');
    const preview = card?.querySelector(':scope > img:not(.main-product__media-preview), .main-product__media-preview, img');
    button.type = 'button';
    button.className = 'yx-pdp-media-viewer__thumb';
    button.dataset.yxViewerIndex = String(index);
    button.setAttribute('role', 'tab');
    button.setAttribute('aria-label', `${COPY.show} ${index + 1}`);
    if (preview instanceof HTMLImageElement) {
      const image = document.createElement('img');
      image.src = preview.currentSrc || preview.src;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      button.append(image);
    } else {
      button.textContent = String(index + 1);
    }
    return button;
  }

  function loadHighResolution(wrap) {
    const image = wrap?.querySelector('.yx-pdp-media-viewer__image');
    const highResolutionUrl = image?.dataset.yxHighRes;
    if (!image || !highResolutionUrl || image.dataset.yxHighResLoaded === 'true') return;
    image.dataset.yxHighResLoaded = 'loading';
    const highResolutionImage = new Image();
    highResolutionImage.decoding = 'async';
    highResolutionImage.onload = async () => {
      try {
        await highResolutionImage.decode();
      } catch (_error) {
        // A loaded image can still be used when decode() is unavailable or rejected.
      }
      image.src = highResolutionUrl;
      image.dataset.yxHighResLoaded = 'true';
    };
    highResolutionImage.onerror = () => {
      delete image.dataset.yxHighResLoaded;
    };
    highResolutionImage.src = highResolutionUrl;
  }

  function resetZoom(state, targetWrap = null) {
    state.scale = 1;
    state.panX = 0;
    state.panY = 0;
    const wraps = targetWrap
      ? [targetWrap]
      : state.dialog.querySelectorAll('.yx-pdp-media-viewer__image-wrap');
    wraps.forEach((wrap) => {
      wrap.classList.remove('is-zoomed', 'is-dragging');
      if (wrap.matches('[role="button"]')) wrap.setAttribute('aria-label', COPY.zoom);
      wrap.style.setProperty('--yx-image-scale', '1');
      wrap.style.setProperty('--yx-pan-x', '0px');
      wrap.style.setProperty('--yx-pan-y', '0px');
      wrap.style.setProperty('--yx-zoom-origin', '50% 50%');
    });
    state.drag = null;
  }

  function suppressTouchClick(state) {
    state.suppressClick = true;
    window.clearTimeout(state.suppressClickTimer);
    state.suppressClickTimer = window.setTimeout(() => {
      state.suppressClick = false;
      state.suppressClickTimer = null;
    }, 320);
  }

  function applyMobileTransform(state, wrap) {
    const image = wrap?.querySelector('.yx-pdp-media-viewer__image');
    if (!image) return;
    const rect = wrap.getBoundingClientRect();
    const imageRatio = image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : 1;
    const wrapRatio = rect.width / rect.height;
    const fittedWidth = imageRatio > wrapRatio ? rect.width : rect.height * imageRatio;
    const fittedHeight = imageRatio > wrapRatio ? rect.width / imageRatio : rect.height;
    const maxX = Math.max(0, (fittedWidth * state.scale - rect.width) / 2);
    const maxY = Math.max(0, (fittedHeight * state.scale - rect.height) / 2);
    state.panX = clamp(state.panX, -maxX, maxX);
    state.panY = clamp(state.panY, -maxY, maxY);
    wrap.classList.toggle('is-zoomed', state.scale > 1.01);
    wrap.style.setProperty('--yx-image-scale', String(state.scale));
    wrap.style.setProperty('--yx-pan-x', `${state.panX}px`);
    wrap.style.setProperty('--yx-pan-y', `${state.panY}px`);
  }

  function applyDesktopTransform(state, wrap) {
    const image = wrap?.querySelector('.yx-pdp-media-viewer__image');
    if (!image) return;
    const rect = wrap.getBoundingClientRect();
    const imageRatio = image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : 1;
    const wrapRatio = rect.width / rect.height;
    const fittedWidth = imageRatio > wrapRatio ? rect.width : rect.height * imageRatio;
    const fittedHeight = imageRatio > wrapRatio ? rect.width / imageRatio : rect.height;
    const maxX = Math.max(0, (fittedWidth * state.scale - rect.width) / 2);
    const maxY = Math.max(0, (fittedHeight * state.scale - rect.height) / 2);
    state.panX = clamp(state.panX, -maxX, maxX);
    state.panY = clamp(state.panY, -maxY, maxY);
    wrap.style.setProperty('--yx-image-scale', String(state.scale));
    wrap.style.setProperty('--yx-pan-x', `${state.panX}px`);
    wrap.style.setProperty('--yx-pan-y', `${state.panY}px`);
  }

  function syncDesktopViewer(state) {
    state.dialog.querySelector('[data-yx-viewer-current]').textContent = String(state.index + 1);
    const thumbs = Array.from(state.dialog.querySelectorAll('[data-yx-viewer-index]'));
    thumbs.forEach((thumb, index) => {
      const active = index === state.index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-selected', String(active));
      thumb.tabIndex = active ? 0 : -1;
    });
    const activeThumb = thumbs[state.index];
    activeThumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    loadHighResolution(state.stage.querySelector(`.yx-pdp-media-viewer__item[data-yx-viewer-position="${state.index}"] .yx-pdp-media-viewer__image-wrap`));
  }

  function syncMobileViewer(state) {
    state.dialog.querySelector('[data-yx-viewer-current]').textContent = String(state.index + 1);
    const items = Array.from(state.stage.querySelectorAll('.yx-pdp-media-viewer__item'));
    items.forEach((item, index) => {
      const active = index === state.index;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', String(!active));
    });
    loadHighResolution(items[state.index]?.querySelector('.yx-pdp-media-viewer__image-wrap'));
    const adjacentIndex = normalise(state.index + 1, state.cards.length);
    loadHighResolution(items[adjacentIndex]?.querySelector('.yx-pdp-media-viewer__image-wrap'));
  }

  function preloadViewerImages(state) {
    const loadAll = () => state.stage.querySelectorAll('.yx-pdp-media-viewer__image-wrap').forEach(loadHighResolution);
    if ('requestIdleCallback' in window) window.requestIdleCallback(loadAll, { timeout: 800 });
    else window.setTimeout(loadAll, 180);
  }

  function scrollDesktopViewer(state, behavior = 'smooth') {
    const left = state.index * state.stage.clientWidth;
    if (behavior === 'auto') {
      state.stage.style.scrollBehavior = 'auto';
      state.stage.scrollLeft = left;
      requestAnimationFrame(() => state.stage.style.removeProperty('scroll-behavior'));
    } else {
      state.stage.scrollTo({ left, behavior });
    }
    syncDesktopViewer(state);
  }

  function toggleDesktopZoom(state, wrap, clientX, clientY) {
    loadHighResolution(wrap);
    if (wrap.classList.contains('is-zoomed')) {
      resetZoom(state, wrap);
      return;
    }
    state.scale = FULL_VIEWER_QUERY.matches ? 2.5 : 2;
    const rect = wrap.getBoundingClientRect();
    state.panX = FULL_VIEWER_QUERY.matches || clientX == null ? 0 : (rect.left + rect.width / 2 - clientX) * (state.scale - 1);
    state.panY = FULL_VIEWER_QUERY.matches || clientY == null ? 0 : (rect.top + rect.height / 2 - clientY) * (state.scale - 1);
    wrap.style.setProperty('--yx-zoom-origin', '50% 50%');
    wrap.classList.add('is-zoomed');
    if (FULL_VIEWER_QUERY.matches) {
      applyDesktopTransform(state, wrap);
      if (wrap.matches('[role="button"]')) wrap.setAttribute('aria-label', COPY.drag);
    } else {
      applyMobileTransform(state, wrap);
      if (wrap.matches('[role="button"]')) wrap.setAttribute('aria-label', COPY.drag);
    }
  }

  function render(state) {
    pauseMedia(state.stage);
    state.stage.replaceChildren();
    const thumbRail = state.dialog.querySelector('[data-yx-viewer-thumbs]');
    thumbRail.replaceChildren();
    if (FULL_VIEWER_QUERY.matches) {
      state.cards.forEach((card, index) => {
        state.stage.append(buildMedia(card, index, state, index));
        thumbRail.append(buildThumbnail(card, index));
      });
      state.stage.style.setProperty('--yx-viewer-count', String(state.cards.length));
      state.dialog.querySelector('[data-yx-viewer-total]').textContent = String(state.cards.length);
      resetZoom(state);
      requestAnimationFrame(() => {
        scrollDesktopViewer(state, 'auto');
        preloadViewerImages(state);
      });
      return;
    }
    state.stage.style.setProperty('--yx-viewer-count', String(state.cards.length));
    state.cards.forEach((card, index) => {
      state.stage.append(buildMedia(card, index, state, index));
    });
    state.dialog.querySelector('[data-yx-viewer-total]').textContent = String(state.cards.length);
    resetZoom(state);
    requestAnimationFrame(() => {
      syncMobileViewer(state);
      preloadViewerImages(state);
    });
  }

  function step(state, delta) {
    state.index = normalise(state.index + delta, state.cards.length);
    if (FULL_VIEWER_QUERY.matches) {
      resetZoom(state);
      scrollDesktopViewer(state);
    }
    else {
      resetZoom(state);
      syncMobileViewer(state);
    }
  }

  function close(state) {
    if (!state || state.closing) return;
    state.closing = true;
    pauseMedia(state.stage);
    state.dialog.classList.remove('is-open');
    const finish = () => {
      if (state.dialog.open) state.dialog.close();
      document.documentElement.classList.remove('yx-pdp-media-viewer-open');
      state.trigger?.focus({ preventScroll: true });
      state.closing = false;
    };
    if (DESKTOP_QUERY.matches && !REDUCED_MOTION_QUERY.matches) window.setTimeout(finish, 220);
    else finish();
  }

  function open(detail) {
    const cards = Array.from(detail.cards || []).filter((card) => sourceMedia(card));
    if (!cards.length) return;
    const dialog = viewer || createViewer();
    viewer = dialog;
    const state = {
      dialog,
      stage: dialog.querySelector('[data-yx-viewer-stage]'),
      cards,
      index: clamp(Number(detail.index) || 0, 0, cards.length - 1),
      trigger: detail.trigger,
      scale: 1,
      panX: 0,
      panY: 0,
      gesture: null,
      scrollFrame: null,
      wheelLockedUntil: 0,
      closing: false,
      drag: null,
      suppressClick: false,
    };
    dialog._yxState = state;
    render(state);
    document.documentElement.classList.add('yx-pdp-media-viewer-open');
    dialog.showModal();
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
    dialog.querySelector('[data-yx-viewer-close]')?.focus({ preventScroll: true });
  }

  document.addEventListener(OPEN_EVENT, (event) => open(event.detail || {}));

  document.addEventListener('click', (event) => {
    const dialog = event.target.closest?.('.yx-pdp-media-viewer');
    const state = dialog?._yxState;
    if (!state) return;
    const closeButton = event.target.closest('[data-yx-viewer-close]');
    if (closeButton) {
      close(state);
      return;
    }
    const stepButton = event.target.closest('[data-yx-viewer-step]');
    if (stepButton) {
      step(state, Number(stepButton.dataset.yxViewerStep));
      return;
    }
    const thumbnail = event.target.closest('[data-yx-viewer-index]');
    if (thumbnail && FULL_VIEWER_QUERY.matches) {
      resetZoom(state);
      state.index = clamp(Number(thumbnail.dataset.yxViewerIndex) || 0, 0, state.cards.length - 1);
      scrollDesktopViewer(state);
      return;
    }
    const videoToggle = event.target.closest('.yx-pdp-media-viewer__video-toggle');
    if (videoToggle) {
      const video = videoToggle.parentElement?.querySelector('video');
      if (!video) return;
      if (video.paused) video.play().catch(() => {});
      else video.pause();
      return;
    }
    const wrap = event.target.closest('.yx-pdp-media-viewer__image-wrap');
    if (!wrap?.closest('.yx-pdp-media-viewer[open]')) return;
    if (state.suppressClick) {
      window.clearTimeout(state.suppressClickTimer);
      state.suppressClickTimer = null;
      state.suppressClick = false;
      return;
    }
    toggleDesktopZoom(state, wrap, event.clientX, event.clientY);
  });

  document.addEventListener('pointerdown', (event) => {
    if (!DESKTOP_QUERY.matches || event.button !== 0) return;
    const wrap = event.target.closest?.('.yx-pdp-media-viewer__image-wrap.is-zoomed');
    const state = wrap?.closest('.yx-pdp-media-viewer')?._yxState;
    if (!state) return;
    state.drag = {
      wrap,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      panX: state.panX,
      panY: state.panY,
      moved: false,
    };
    wrap.classList.add('is-dragging');
    wrap.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  });

  document.addEventListener('pointermove', (event) => {
    const state = viewer?._yxState;
    const drag = state?.drag;
    if (!drag || drag.pointerId !== event.pointerId || !DESKTOP_QUERY.matches) return;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    drag.moved ||= Math.hypot(deltaX, deltaY) > 3;
    state.panX = drag.panX + deltaX;
    state.panY = drag.panY + deltaY;
    applyDesktopTransform(state, drag.wrap);
    event.preventDefault();
  });

  function finishDesktopDrag(event) {
    const state = viewer?._yxState;
    const drag = state?.drag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    drag.wrap.classList.remove('is-dragging');
    drag.wrap.releasePointerCapture?.(event.pointerId);
    state.suppressClick = drag.moved;
    state.drag = null;
  }

  document.addEventListener('pointerup', finishDesktopDrag);
  document.addEventListener('pointercancel', finishDesktopDrag);

  document.addEventListener('pointerover', (event) => {
    const wrap = event.target.closest?.('.yx-pdp-media-viewer__image-wrap');
    if (!wrap?.closest('.yx-pdp-media-viewer[open]')) return;
    loadHighResolution(wrap);
  });

  document.addEventListener('pointermove', (event) => {
    if (MOBILE_QUERY.matches || DESKTOP_QUERY.matches) return;
    const wrap = event.target.closest?.('.yx-pdp-media-viewer__image-wrap.is-zoomed');
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const x = clamp((event.clientX - rect.left) / rect.width * 100, 0, 100);
    const y = clamp((event.clientY - rect.top) / rect.height * 100, 0, 100);
    wrap.style.setProperty('--yx-zoom-origin', `${x}% ${y}%`);
  });

  document.addEventListener('keydown', (event) => {
    const state = viewer?._yxState;
    if (!viewer?.open || !state) return;
    const wrap = event.target.closest?.('.yx-pdp-media-viewer__image-wrap[role="button"]');
    if (wrap && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      toggleDesktopZoom(state, wrap);
      return;
    }
    if (event.key === 'ArrowLeft') step(state, -1);
    if (event.key === 'ArrowRight') step(state, 1);
  });

  document.addEventListener('cancel', (event) => {
    if (!event.target.matches?.('.yx-pdp-media-viewer')) return;
    event.preventDefault();
    close(event.target._yxState);
  }, true);

  document.addEventListener('touchstart', (event) => {
    const wrap = event.target.closest?.('.yx-pdp-media-viewer__image-wrap');
    const state = wrap?.closest('.yx-pdp-media-viewer')?._yxState;
    if (!state || !TOUCH_QUERY.matches) return;
    if (event.touches.length === 2 && MOBILE_QUERY.matches) {
      const first = event.touches[0];
      const second = event.touches[1];
      const midpoint = touchMidpoint(first, second);
      const rect = wrap.getBoundingClientRect();
      state.gesture = {
        mode: 'pinch',
        wrap,
        startDistance: touchDistance(first, second),
        startScale: state.scale,
        anchorX: (midpoint.x - (rect.left + rect.width / 2) - state.panX) / state.scale,
        anchorY: (midpoint.y - (rect.top + rect.height / 2) - state.panY) / state.scale,
      };
      event.preventDefault();
      return;
    }
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    state.gesture = {
      mode: state.scale > 1.01 ? 'pan' : 'swipe',
      wrap,
      startX: touch.clientX,
      startY: touch.clientY,
      panX: state.panX,
      panY: state.panY,
      moved: false,
    };
  }, { passive: false });

  document.addEventListener('touchmove', (event) => {
    const state = viewer?._yxState;
    const gesture = state?.gesture;
    if (!viewer?.open || !gesture || !TOUCH_QUERY.matches) return;
    if (gesture.mode === 'pinch' && event.touches.length === 2) {
      const first = event.touches[0];
      const second = event.touches[1];
      const midpoint = touchMidpoint(first, second);
      const rect = gesture.wrap.getBoundingClientRect();
      state.scale = clamp(gesture.startScale * touchDistance(first, second) / Math.max(1, gesture.startDistance), 1, 4);
      state.panX = midpoint.x - (rect.left + rect.width / 2) - gesture.anchorX * state.scale;
      state.panY = midpoint.y - (rect.top + rect.height / 2) - gesture.anchorY * state.scale;
      applyMobileTransform(state, gesture.wrap);
      if (gesture.wrap.matches('[role="button"]')) {
        gesture.wrap.setAttribute('aria-label', state.scale > 1.01 ? COPY.drag : COPY.zoom);
      }
      event.preventDefault();
      return;
    }
    if (gesture.mode === 'pan' && event.touches.length === 1) {
      const touch = event.touches[0];
      gesture.moved ||= Math.hypot(touch.clientX - gesture.startX, touch.clientY - gesture.startY) > 6;
      state.panX = gesture.panX + touch.clientX - gesture.startX;
      state.panY = gesture.panY + touch.clientY - gesture.startY;
      if (FULL_VIEWER_QUERY.matches) applyDesktopTransform(state, gesture.wrap);
      else applyMobileTransform(state, gesture.wrap);
      event.preventDefault();
    }
  }, { passive: false });

  document.addEventListener('touchend', (event) => {
    const state = viewer?._yxState;
    const gesture = state?.gesture;
    if (!viewer?.open || !gesture || !TOUCH_QUERY.matches) return;
    if (gesture.mode === 'pinch') {
      suppressTouchClick(state);
      if (state.scale <= 1.05) resetZoom(state, gesture.wrap);
      else if (gesture.wrap.matches('[role="button"]')) gesture.wrap.setAttribute('aria-label', COPY.drag);
      state.gesture = null;
      return;
    }
    if (gesture.mode === 'swipe' && event.changedTouches.length) {
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - gesture.startX;
      const deltaY = touch.clientY - gesture.startY;
      if (MOBILE_QUERY.matches && Math.hypot(deltaX, deltaY) > 8) suppressTouchClick(state);
      if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY) * 1.3) step(state, deltaX < 0 ? 1 : -1);
    } else if (gesture.mode === 'pan') {
      if (MOBILE_QUERY.matches && gesture.moved) suppressTouchClick(state);
    }
    state.gesture = null;
  }, { passive: true });

  MOBILE_QUERY.addEventListener('change', () => {
    const state = viewer?._yxState;
    if (viewer?.open && state) render(state);
  });

  FULL_VIEWER_QUERY.addEventListener('change', () => {
    const state = viewer?._yxState;
    if (viewer?.open && state) render(state);
  });
})();
